import React, { useState, useEffect, useRef, useCallback } from 'react';
import { dataService } from '../services/dataService';
import { confirmDialog, alertDialog } from '../utils/dialog';

const FILE_ICONS = {
  'application/pdf': '📄',
  'image/png': '🖼',
  'image/jpeg': '🖼',
  'image/gif': '🖼',
  'image/webp': '🖼',
  'image/svg+xml': '🖼',
  'text/plain': '📝',
  'text/csv': '📊',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '📊',
  'application/vnd.ms-excel': '📊',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '📃',
  'application/msword': '📃',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '📽',
  'application/json': '{ }',
  'application/zip': '📦',
};

function fileIcon(contentType) {
  return FILE_ICONS[contentType] || '📎';
}

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function timeAgo(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const MAX_FILE_SIZE = 25 * 1048576; // 25 MB

export default function DocumentationView({ workspaceId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState('All');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [search, setSearch] = useState('');
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const [preview, setPreview] = useState(null);
  const [movingDoc, setMovingDoc] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    if (!workspaceId) { setLoading(false); return; }
    setLoading(true);
    const unsub = dataService.subscribeDocuments(workspaceId, (items) => {
      setDocs(items);
      setLoading(false);
    });
    return () => unsub && unsub();
  }, [workspaceId]);

  useEffect(() => {
    const close = () => setContextMenu(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, []);

  const folders = ['All', ...Array.from(new Set(docs.map(d => d.folder || 'General'))).sort()];

  const filtered = docs.filter(d => {
    if (activeFolder !== 'All' && d.folder !== activeFolder) return false;
    if (search && !d.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const uploadFiles = useCallback(async (files) => {
    if (!workspaceId || !files.length) return;
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        await alertDialog(`"${file.name}" exceeds the 25 MB limit.`);
        continue;
      }
      setUploading(true);
      setUploadProgress(0);
      try {
        await dataService.uploadDocument(
          workspaceId,
          file,
          activeFolder === 'All' ? 'General' : activeFolder,
          (pct) => setUploadProgress(pct)
        );
      } catch (err) {
        console.error('Upload failed:', err);
        await alertDialog(`Upload failed: ${err.message}`);
      }
    }
    setUploading(false);
    setUploadProgress(0);
  }, [workspaceId, activeFolder]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(Array.from(e.dataTransfer.files));
  };

  const handleDelete = async (d) => {
    if (!(await confirmDialog({ title: 'Delete document', message: `Delete "${d.name}"?`, confirmText: 'Delete', danger: true }))) return;
    await dataService.deleteDocument(workspaceId, d.id, d.storagePath);
  };

  const handleRename = async (d) => {
    const trimmed = renameVal.trim();
    if (!trimmed || trimmed === d.name) { setRenaming(null); return; }
    await dataService.renameDocument(workspaceId, d.id, trimmed);
    setRenaming(null);
  };

  const handleMove = async (targetFolder) => {
    if (!movingDoc) return;
    await dataService.moveDocument(workspaceId, movingDoc.id, targetFolder);
    setMovingDoc(null);
  };

  const handleCreateFolder = () => {
    const name = newFolderName.trim();
    if (!name) return;
    setActiveFolder(name);
    setNewFolderOpen(false);
    setNewFolderName('');
  };

  const isPreviewable = (ct) =>
    ct?.startsWith('image/') || ct === 'application/pdf' || ct === 'text/plain';

  return (
    <div
      className="docs-view"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      {/* Sidebar — folders */}
      <div className="docs-sidebar">
        <div className="docs-sidebar-header">Documents</div>
        <nav className="docs-nav">
          {folders.map(f => (
            <button
              key={f}
              className={`docs-nav-item ${activeFolder === f ? 'active' : ''}`}
              onClick={() => setActiveFolder(f)}
            >
              <span className="docs-nav-icon">{f === 'All' ? '📁' : '📂'}</span>
              {f}
              <span className="docs-folder-count">
                {f === 'All' ? docs.length : docs.filter(d => d.folder === f).length}
              </span>
            </button>
          ))}
          {newFolderOpen ? (
            <div className="docs-new-folder-row">
              <input
                className="docs-new-folder-input"
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') setNewFolderOpen(false); }}
                placeholder="Folder name"
                autoFocus
              />
              <button className="docs-new-folder-ok" onClick={handleCreateFolder}>+</button>
            </div>
          ) : (
            <button className="docs-nav-item docs-add-folder" onClick={() => setNewFolderOpen(true)}>
              <span className="docs-nav-icon">+</span>
              New folder
            </button>
          )}
        </nav>
        <div className="docs-sidebar-footer">
          <span className="docs-footer-note">Upload files for your team and<br/>future AI agents to reference</span>
        </div>
      </div>

      {/* Main area */}
      <div className="docs-main">
        <div className="docs-toolbar">
          <div className="docs-toolbar-left">
            <h2 className="docs-toolbar-title">
              {activeFolder === 'All' ? 'All Documents' : activeFolder}
            </h2>
            <span className="docs-toolbar-count">{filtered.length} file{filtered.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="docs-toolbar-right">
            <input
              className="docs-search"
              placeholder="Search files..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button className="docs-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? `Uploading ${uploadProgress}%` : 'Upload'}
            </button>
            <input
              ref={fileRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={e => { uploadFiles(Array.from(e.target.files)); e.target.value = ''; }}
            />
          </div>
        </div>

        {loading ? (
          <div className="docs-empty">Loading documents...</div>
        ) : filtered.length === 0 && docs.length === 0 ? (
          <div className={`docs-dropzone ${dragOver ? 'drag-over' : ''}`}>
            <div className="docs-empty-icon">📂</div>
            <div className="docs-empty-title">No documents yet</div>
            <div className="docs-empty-sub">
              Drag and drop files here, or click Upload to add documents.<br/>
              SOPs, contracts, guides, templates — anything your team or AI agents need.
            </div>
            <button className="docs-upload-btn" onClick={() => fileRef.current?.click()}>
              Upload files
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="docs-empty">
            <div className="docs-empty-icon">🔍</div>
            <div className="docs-empty-title">No matches</div>
            <div className="docs-empty-sub">Try a different search or folder.</div>
          </div>
        ) : (
          <div className={`docs-file-list ${dragOver ? 'drag-over' : ''}`}>
            <div className="docs-file-header">
              <span className="docs-fh-name">Name</span>
              <span className="docs-fh-folder">Folder</span>
              <span className="docs-fh-size">Size</span>
              <span className="docs-fh-date">Uploaded</span>
              <span className="docs-fh-actions" />
            </div>
            {filtered.map(d => (
              <div
                key={d.id}
                className="docs-file-row"
                onContextMenu={(e) => { e.preventDefault(); setContextMenu({ x: e.clientX, y: e.clientY, doc: d }); }}
              >
                <span className="docs-file-icon">{fileIcon(d.contentType)}</span>
                <span className="docs-file-name">
                  {renaming === d.id ? (
                    <input
                      className="docs-rename-input"
                      value={renameVal}
                      onChange={e => setRenameVal(e.target.value)}
                      onBlur={() => handleRename(d)}
                      onKeyDown={e => { if (e.key === 'Enter') handleRename(d); if (e.key === 'Escape') setRenaming(null); }}
                      autoFocus
                    />
                  ) : (
                    <span
                      className="docs-file-name-text"
                      onClick={() => isPreviewable(d.contentType) ? setPreview(d) : window.open(d.url, '_blank')}
                      title={d.name}
                    >
                      {d.name}
                    </span>
                  )}
                </span>
                <span className="docs-file-folder">{d.folder}</span>
                <span className="docs-file-size">{formatBytes(d.size)}</span>
                <span className="docs-file-date">{timeAgo(d.uploadedAt)}</span>
                <span className="docs-file-actions">
                  <a href={d.url} target="_blank" rel="noopener noreferrer" className="docs-action-btn" title="Download">↓</a>
                  <button className="docs-action-btn" title="Rename" onClick={() => { setRenaming(d.id); setRenameVal(d.name); }}>✎</button>
                  <button className="docs-action-btn docs-action-del" title="Delete" onClick={() => handleDelete(d)}>✕</button>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Context menu */}
      {contextMenu && (
        <div className="docs-ctx-menu" style={{ left: contextMenu.x, top: contextMenu.y }} onClick={e => e.stopPropagation()}>
          <button className="docs-ctx-item" onClick={() => { window.open(contextMenu.doc.url, '_blank'); setContextMenu(null); }}>Download</button>
          <button className="docs-ctx-item" onClick={() => { setRenaming(contextMenu.doc.id); setRenameVal(contextMenu.doc.name); setContextMenu(null); }}>Rename</button>
          <button className="docs-ctx-item" onClick={() => { setMovingDoc(contextMenu.doc); setContextMenu(null); }}>Move to folder...</button>
          {isPreviewable(contextMenu.doc.contentType) && (
            <button className="docs-ctx-item" onClick={() => { setPreview(contextMenu.doc); setContextMenu(null); }}>Preview</button>
          )}
          <div className="docs-ctx-divider" />
          <button className="docs-ctx-item docs-ctx-danger" onClick={() => { handleDelete(contextMenu.doc); setContextMenu(null); }}>Delete</button>
        </div>
      )}

      {/* Move to folder modal */}
      {movingDoc && (
        <div className="docs-modal-overlay" onClick={() => setMovingDoc(null)}>
          <div className="docs-modal" onClick={e => e.stopPropagation()}>
            <div className="docs-modal-title">Move "{movingDoc.name}" to...</div>
            <div className="docs-modal-folders">
              {folders.filter(f => f !== 'All' && f !== movingDoc.folder).map(f => (
                <button key={f} className="docs-modal-folder-btn" onClick={() => handleMove(f)}>{f}</button>
              ))}
            </div>
            <div className="docs-modal-actions">
              <button className="docs-modal-cancel" onClick={() => setMovingDoc(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="docs-modal-overlay" onClick={() => setPreview(null)}>
          <div className="docs-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="docs-preview-header">
              <span className="docs-preview-name">{preview.name}</span>
              <div className="docs-preview-btns">
                <a href={preview.url} target="_blank" rel="noopener noreferrer" className="docs-action-btn" title="Download">↓</a>
                <button className="docs-action-btn" onClick={() => setPreview(null)} title="Close">✕</button>
              </div>
            </div>
            <div className="docs-preview-body">
              {preview.contentType?.startsWith('image/') ? (
                <img src={preview.url} alt={preview.name} className="docs-preview-img" />
              ) : preview.contentType === 'application/pdf' ? (
                <iframe src={preview.url} title={preview.name} className="docs-preview-iframe" />
              ) : (
                <iframe src={preview.url} title={preview.name} className="docs-preview-iframe" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
